import { Tooltip, Box, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { DataGrid, GridColumnHeaderParams } from '@mui/x-data-grid'
import type { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { JobRowData } from '@/api/definition'
import {
  fetchPaginatedJobs,
  postCancelJobs,
  postCompleteConfirmJobs,
} from '@/api/fota'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { Installed } from '@/pages/fota/jobState'
import {
  DateTime,
  FotaJobStateFormatter,
  IsOnline,
  Uid,
} from '@/pages/utils/formatter'
import { useFotaInstalledFilters } from '@/hooks/useTransalationForFilter'
import { useFilters } from '@/hooks/useFilters'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import { FilterDrawer } from '@/components/FilterDrawer'
import { useTableUrlState } from '@/hooks/useTableUrlState'

type Props = {
  autoRefresh?: boolean
  enableFilters?: boolean
  enableSearch?: boolean
}

const ListInstalledJobs = ({
  autoRefresh,
  enableFilters = false,
  enableSearch = true,
}: Props): React.JSX.Element => {
  const { t } = useTranslation(['di', 'app', 'common'])
  const { enqueueSnackbar } = useSnackbar()
  const filterConfig = useFotaInstalledFilters()

  // URL state hook
  const { urlState, updateUrlState } = useTableUrlState()

  // Local filters (just for drawer state)
  const localFilterHooks = useFilters()

  // State management - use URL state
  const [data, setData] = useState<JobRowData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [rowCount, setRowCount] = useState<number>(0)
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])

  // Local search state
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout>()

  // Refs for managing state and intervals
  const mountedRef = useRef(false)
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Use URL state for these values
  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel
  const selectedFilters = urlState.filters

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
        renderCell: (params) => <Uid value={params.value} />,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'is_online',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 80,
        renderCell: (params) => <IsOnline value={params.value} />,
      },
      {
        field: 'created_by',
        headerName: t('di:fota.createdBy'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'version',
        headerName: t('di:fota.firmwareVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'state',
        headerName: t('di:fota.state'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        renderCell: (params) => <FotaJobStateFormatter state={params.value} />,
      },
      {
        field: 'updated_at',
        headerName: t('di:fota.lastChange'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        renderCell: (params) => <DateTime value={params.value} />,
      },
    ],
    [t],
  )

  const loadData = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSortModel: GridSortModel,
      currentSearchTerm: string,
      isAutoRefresh = false,
    ) => {
      if (!mountedRef.current) return

      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      if (!isAutoRefresh) setLoading(true)

      try {
        const hasSortModel = currentSortModel.length > 0
        let sortParam = 'updated_at'
        if (hasSortModel) {
          sortParam =
            currentSortModel[0].field === 'state'
              ? 'State'
              : currentSortModel[0].field
        }
        const sortDirection = hasSortModel ? currentSortModel[0].sort : 'desc'

        const { data: jobData, totalRows } = await fetchPaginatedJobs({
          page: currentPage,
          pageSize: currentPageSize,
          sortBy: sortParam,
          sortDir: sortDirection,
          searchTerm: currentSearchTerm.trim(),
          state: [Installed],
        })

        if (!jobData) throw new Error('No data received from server')

        const canUpdate =
          mountedRef.current && !abortControllerRef.current?.signal.aborted

        if (canUpdate) {
          setData(jobData)
          setRowCount(totalRows)
        }
      } catch (error) {
        const canShowError =
          mountedRef.current && !abortControllerRef.current?.signal.aborted
        if (canShowError) {
          console.error('Error fetching data:', error)
          enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
        }
      } finally {
        if (mountedRef.current && !isAutoRefresh) {
          setLoading(false)
        }
      }
    },
    [enqueueSnackbar, t],
  )

  // Single effect that watches URL state
  useEffect(() => {
    mountedRef.current = true
    loadData(page, pageSize, sortModel, searchTerm)

    return () => {
      mountedRef.current = false
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [page, pageSize, sortModel, searchTerm, loadData])

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && mountedRef.current) {
      autoRefreshIntervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          loadData(page, pageSize, sortModel, searchTerm, true)
        }
      }, 30000)

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current)
          autoRefreshIntervalRef.current = null
        }
      }
    }
  }, [autoRefresh, page, pageSize, sortModel, searchTerm, loadData])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update (300ms)
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...selectedFilters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0,
        filters: newFilters,
      })
    },
    [selectedFilters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = selectedFilters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [selectedFilters, handleFilterChange],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: model,
      })
    },
    [updateUrlState],
  )

  const handleCancel = useCallback(async () => {
    try {
      const result = await postCancelJobs(selectedRows.map(Number))
      const failCount = result.filter((i) => !i.successful).length
      const passCount = result.filter((i) => i.successful).length

      if (failCount > 0 && passCount > 0) {
        enqueueSnackbar(
          t('di:fota.fotaCancelJobsToastsMessages.partiallyCompleted', {
            failCount: failCount.toString(),
            passCount: passCount.toString(),
          }),
          { variant: 'warning' },
        )
      } else if (failCount === 0 && passCount > 0) {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.success'), {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.failed'), {
          variant: 'error',
        })
      }

      loadData(page, pageSize, sortModel, searchTerm)
      setSelectedRows([])
      setOpenCancelDialog(false)
    } catch (error) {
      console.error('Error canceling jobs:', error)
      enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
    }
  }, [
    selectedRows,
    enqueueSnackbar,
    t,
    loadData,
    page,
    pageSize,
    sortModel,
    searchTerm,
  ])

  const handleConfirmJob = useCallback(async () => {
    setLoading(true)
    try {
      const result = await postCompleteConfirmJobs(selectedRows.map(Number))
      const failCount = result.filter((i) => !i.successful).length
      const passCount = result.filter((i) => i.successful).length

      if (failCount > 0 && passCount > 0) {
        enqueueSnackbar(
          t(
            'di:fota.fotaCompleteInstalledJobsToastsMessages.partiallyCompleted',
            {
              failCount: failCount.toString(),
              passCount: passCount.toString(),
            },
          ),
          { variant: 'warning' },
        )
      } else if (failCount === 0 && passCount > 0) {
        enqueueSnackbar(
          t('di:fota.fotaCompleteInstalledJobsToastsMessages.success'),
          { variant: 'success' },
        )
      } else {
        enqueueSnackbar(
          t('di:fota.fotaCompleteInstalledJobsToastsMessages.failed'),
          { variant: 'error' },
        )
      }

      loadData(page, pageSize, sortModel, searchTerm)
    } catch (error) {
      console.error('Error during job confirmation:', error)
      enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
    } finally {
      setLoading(false)
      setSelectedRows([])
      setOpenConfirmDialog(false)
    }
  }, [
    selectedRows,
    enqueueSnackbar,
    t,
    loadData,
    page,
    pageSize,
    sortModel,
    searchTerm,
  ])

  const shouldShowCustomToolbar = enableFilters || enableSearch

  const activeFilterCount = enableFilters
    ? Object.values(selectedFilters).reduce(
        (count, group) => count + Object.keys(group).length,
        0,
      )
    : 0

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
        }}
      >
        <DataGrid
          rowHeight={45}
          rows={data}
          columns={columns}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          loading={loading}
          checkboxSelection
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={rowCount}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          onSortModelChange={handleSortModelChange}
          onRowSelectionModelChange={(ids) => setSelectedRows(ids as number[])}
          slots={
            shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
          }
          slotProps={{
            toolbar: shouldShowCustomToolbar
              ? ({
                  onFilterClick: enableFilters
                    ? localFilterHooks.openDrawer
                    : undefined,
                  activeFilterCount: activeFilterCount,
                  enableFilters: enableFilters,
                  enableSearch: enableSearch,
                  selectedFilters: selectedFilters,
                  filterConfig: filterConfig,
                  onRemoveFilter: handleRemoveFilter,
                  searchValue: localSearchTerm,
                  onSearchChange: handleSearchChange,
                } as CustomToolbarProps)
              : undefined,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          localeText={{
            noRowsLabel: 'No jobs found',
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {enableFilters && filterConfig && (
        <FilterDrawer
          open={localFilterHooks.isDrawerOpen}
          onClose={localFilterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
        />
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Tooltip title={t('device.fotaJobs.complete')}>
          <span>
            <Button
              variant="contained"
              color="success"
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.65,
                },
              }}
              disabled={selectedRows.length === 0}
              onClick={() => setOpenConfirmDialog(true)}
            >
              {t('device.fotaJobs.complete')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t('device.fotaJobs.cancel')}>
          <span>
            <Button
              variant="contained"
              disabled={!selectedRows.length || loading}
              onClick={() => setOpenCancelDialog(true)}
              sx={{
                backgroundColor: '#ff9800',
                '&.Mui-disabled': {
                  opacity: 0.65,
                  backgroundColor: '#ff9800',
                  color: '#fff',
                },
              }}
            >
              {t('device.fotaJobs.cancel')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmJob}
        setOpen={setOpenConfirmDialog}
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmCompleteInstalledJobs')}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        onConfirm={handleCancel}
        setOpen={setOpenCancelDialog}
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmCancelInstalledJobs')}
        </Typography>
      </ConfirmDialog>
    </Box>
  )
}

export default ListInstalledJobs
